import React, { useState } from "react";
import useRegistrationContext from "../../../../hooks/useRegistrationContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "react-query";
import { createApplication } from "../../../../api/apiServices";
import toast, { Toaster } from "react-hot-toast";
import { format, sub } from "date-fns";
import { useContext } from "react";
import AuthContext from "../../../../context/AuthProvider";
import { Messages } from "./Messages";
import RegistrationFormInputs from "./RegistrationFormInputs";
import Loader from "../../../Common/Spinner/Loader";

const FormSetup = () => {
	const { auth } = useContext(AuthContext);

	const navigate = useNavigate();
	const location = useLocation();
	const from = location.state?.from?.pathname || "/successful_application";

	let [isLoading, setIsLoading] = useState(false);

	const {
		page,
		setPage,
		data,
		setData,
		registrationForms,
		canSubmit,
		disablePrev,
		disableNext,
		prevHide,
		nextHide,
		submitHide,
	} = useRegistrationContext();

	const handlePrevious = () => setPage((prev) => prev - 1);
	const handleNext = () => setPage((prev) => prev + 1);

	const handleSubmit = async () => {
		setIsLoading(true);
		const minDate = sub(new Date(), {
			months: 216,
		});
		const dateFormat = "yyyy-MM-dd";
		const dateOfBirth = data.date_of_birth;
		const memDOB = new Date(dateOfBirth);
		const today = new Date();
		const distance = today.getTime() - memDOB.getTime();
		const daysOld = Math.floor(distance / (1000 * 60 * 60 * 24));
		const memAge = Number((daysOld / 365).toFixed(0));

		if (memAge < 18) {
			toast.error(
				`The acceptable date of birth limit is ${format(
					minDate,
					dateFormat
				)} and below`
			);
			setIsLoading(false);
			return false;
		}

		let formData = new FormData();

		formData.append("file", data.personalProfilePicture);
		formData.set("registered_ndc_member", data.membershipNdcMembership);
		formData.set(
			"ndc_membership_number",
			data.membershipNdcMembershipNumber
		);
		formData.set("voter_id_number", data.membershipVoterIdNumber);

		formData.set("title", data.personalTitle);
		formData.set("last_name", data.personalLastname);
		formData.set("first_name", data.personalFirstname);
		formData.set("middle_name", data.personalMiddleName);
		formData.set("date_of_birth", data.personalDateOfBirth);
		formData.set("gender", data.personalGender);
		formData.set("marital_status", data.personalMaritalStatus);

		formData.set("mobile_number", data.contactMobileNumber);
		formData.set("email", data.contactEmail);

		formData.set("region_code", data.regionsRegionCode);
		formData.set("region", data.regionsRegionName);
		formData.set("constituency_code", data.regionsConstituencyCode);
		formData.set("constituency", data.regionsConstituencyName);
		formData.set("branch_code", data.regionsBranchCode);
		formData.set("branch", data.regionsBranchName);

		formData.set(
			"regional_president_email",
			data.regionsRegionalPresidentEmail
		);
		formData.set("recommended_by", data.regionsRecommendedBy);

		formData.set(
			"last_tertiary_institution",
			data.educationLastTertiaryInstitution
		);
		formData.set("qualification_type", data.educationQualificationType);
		formData.set("qualification", data.educationQualification);
		formData.set("profession", data.professionProfession);
		formData.set("occupations", data.professionOccupation);
		formData.set("employment_type", data.professionEmploymentType);
		formData.set("current_employment", data.professionCurrentEmployment);

		createApplicationMutation.mutate(formData);
	};

	const createApplicationMutation = useMutation(createApplication, {
		onSuccess: async (res) => {
			if (!res.success) {
				toast.error(res.message, {
					duration: 5000,
					className: "toast-message",
				});
				setIsLoading(false);
				return false;
			} else {
				setIsLoading(false);
				toast.success(res.message, {
					duration: 5000,
					className: "toast-message",
				});
				navigate(-1);
			}
		},
		onError: async (err) => {
			toast.error(err.message, {
				duration: 5000,
				className: "toast-message",
			});
		},
	});

	let applicationForms;
	if (isLoading) {
		applicationForms = (
			<div className="loader">
				<p>Submitting application. Please wait...</p>
				<Loader />
			</div>
		);
	} else {
		applicationForms = (
			<form>
				<RegistrationFormInputs />
			</form>
		);
	}

	const content = (
		<>
			<section className="content-header">
				<Toaster toastOptions={{ position: "top-center" }} />
				<h1>PROFORUM Member Registration</h1>
			</section>
			<section className="content">
				<div className="box">
					<div className="box-body">
						<div className="panel panel-default panel-br-4">
							<div className="panel-heading">
								<div className="row">
									<div className="col-sm-12">
										<h4 className="panel-title">
											{
												registrationForms
													.memberRegistration[page]
											}
										</h4>
									</div>
								</div>
							</div>
							<div className="panel-body">{applicationForms}</div>
							<div className="panel-footer">
								<div className="row">
									{!disablePrev && (
										<div className="col-sm-1">
											<button
												type="button"
												className={`btn btn-primary  btn-block ${prevHide}`}
												onClick={handlePrevious}
												disabled={disablePrev}
											>
												Previous
											</button>
										</div>
									)}
									&nbsp;&nbsp;
									{!disableNext && data.canGoNext && (
										<div className="col-sm-1">
											<button
												type="button"
												className={`btn btn-danger  btn-block ${nextHide}`}
												onClick={handleNext}
												disabled={disableNext}
											>
												Next
											</button>
										</div>
									)}
									<div className="col-sm-1">
										<button
											type="button"
											onClick={handleSubmit}
											className={`btn btn-success btn-block ${submitHide}`}
											disabled={!canSubmit}
										>
											Submit
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</>
	);

	return content;
};
export default FormSetup;
